<template>
  <div class="chart-card">
    <div class="chart-card-header">
      <Dropdown
        :titleDropdown="selectedChart.name"
        position="right"
        colorArrow="black"
      >
        <ul>
          <li
            v-for="chart in availableCharts"
            :key="chart.type"
            @click="changeChartType(chart.type)"
            class="bkps-dropdown-item"
            :title="chart.description"
          >
            <i :class="chart.icon"></i>
            {{ chart.name }}
          </li>
        </ul>
      </Dropdown>

      <Dropdown
        :titleDropdown="selectedSort.name"
        position="right"
        colorArrow="black"
      >
        <ul>
          <li
            v-for="sort in availableSorts"
            :key="sort.type"
            @click="changeSortType(sort.type)"
            class="bkps-dropdown-item"
            :title="sort.description"
          >
            <i :class="sort.icon"></i>
            {{ sort.name }}
          </li>
        </ul>
      </Dropdown>
    </div>
    <div ref="targetChart" style="height: 400px; width: 100%"></div>
  </div>
</template>

<script>
import Highcharts from 'highcharts/es5/highcharts'
import drilldown from 'highcharts/es5/modules/drilldown'
import accessibility from 'highcharts/es5/modules/accessibility'

import ChartMix from '@/components/charts/ChartMix.vue'
import ChartThemeMixin from '@/components/charts/ChartThemeMixin.vue'
import Dropdown from '@/components/shares/specialTag/Dropdown.vue'

drilldown(Highcharts)
accessibility(Highcharts)

const calculatePercentage = (value, total) => (total ? (value * 100) / total : 0)
const withoutClassification = 'SEM CLASSIFICAÇÃO'

export default {
  components: { Dropdown },
  props: {
    classifications: { required: true, type: Array },
    showDetails: { type: Boolean, default: false }
  },
  data() {
    return {
      currentType: 'pie',
      sortBy: 'value',
      chartInstance: null,
      availableCharts: [
        {
          type: 'pie',
          name: 'Pizza',
          icon: 'fas fa-chart-pie',
          description: 'Gráfico de pizza com drill-down'
        },
        {
          type: 'donut',
          name: 'Donut',
          icon: 'fas fa-dot-circle',
          description: 'Gráfico donut moderno'
        },
        {
          type: 'bar',
          name: 'Barras',
          icon: 'fas fa-chart-bar',
          description: 'Gráfico de barras'
        },
        {
          type: 'line',
          name: 'Linha',
          icon: 'fas fa-chart-line',
          description: 'Gráfico de linhas'
        }
      ],
      availableSorts: [
        {
          type: 'value',
          name: 'Valor',
          icon: 'fas fa-dollar-sign',
          description: 'Exibir por valor (R$)'
        },
        {
          type: 'count',
          name: 'Qtd',
          icon: 'fas fa-hashtag',
          description: 'Exibir por quantidade'
        }
      ]
    }
  },
  computed: {
    selectedChart() {
      return this.availableCharts.find(chart => chart.type === this.currentType)
    },
    selectedSort() {
      return this.availableSorts.find(sort => sort.type === this.sortBy)
    }
  },
  methods: {
    changeChartType(type) {
      this.currentType = type
      this.createChart()
    },
    changeSortType(type) {
      this.sortBy = type
      this.createChart()
    },
    buildSeries() {
      const serie = []
      const subserie = []
      const mapSum = {}
      const mapCount = {}
      const isCount = this.sortBy === 'count'

      this.classifications.forEach(c => {
        const serieName = c.ticketClass || withoutClassification
        mapSum[serieName] = (mapSum[serieName] || 0) + c.sum
        mapCount[serieName] = (mapCount[serieName] || 0) + c.count
      })

      const totalSum = Object.values(mapSum).reduce((a, b) => a + b, 0)
      const totalCount = Object.values(mapCount).reduce((a, b) => a + b, 0)
      const total = isCount ? totalCount : totalSum
      const mapMetric = isCount ? mapCount : mapSum

      this.classifications.forEach(c => {
        const serieName = c.ticketClass || withoutClassification
        const subserieName = c.ticketSubclass || withoutClassification
        const record = subserie.find(t => t.name === serieName)

        const subMetric = isCount ? c.count : c.sum
        const subMetricTotal = mapMetric[serieName]

        const subPoint = {
          name: subserieName,
          y: calculatePercentage(subMetric, subMetricTotal),
          rawValue: c.sum / 100,
          count: c.count
        }

        if (record) {
          record.data.push(subPoint)
        } else {
          subserie.push({
            name: serieName,
            id: serieName,
            data: [subPoint],
          })
        }
      })

      subserie.forEach(record => {
        serie.push({
          name: record.name,
          y: calculatePercentage(mapMetric[record.name], total),
          drilldown: record.name,
          rawValue: mapSum[record.name] / 100,
          count: mapCount[record.name] || 0
        })
      })

      return [serie, subserie]
    },
    createChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy()
        this.chartInstance = null
      }

      const [serie, subserie] = this.buildSeries()
      const theme = this.chartTheme
      const sortBy = this.sortBy
      let chartConfig = {}

      const colorConnectors = function (chart) {
        const series = chart.series && chart.series[0]
        if (!series) return
        series.points.forEach(function (point) {
          if (point.connector) {
            point.connector.attr({ stroke: point.color })
          }
        })
      }

      const yAxisTitle = sortBy === 'value'
        ? 'Porcentagem por Valor (%)'
        : 'Porcentagem por Quantidade (%)'

      if (this.currentType === 'pie') {
        chartConfig = {
          chart: {
            type: 'pie',
            backgroundColor: theme.backgroundColor,
            height: 400,
            style: { fontFamily: '"Montserrat", sans-serif' },
            events: { render: function () { colorConnectors(this) } }
          },
          plotOptions: {
            pie: {
              borderRadius: 8,
              borderWidth: 2,
              borderColor: theme.backgroundColor,
              dataLabels: {
                enabled: true,
                distance: 10,
                connectorWidth: 1,
                connectorPadding: 3,
                style: {
                  fontWeight: '600',
                  fontSize: '11px',
                  color: theme.textColor,
                  textOutline: 'none'
                },
                format: '{point.name}: {point.y:.1f}%'
              }
            }
          },
          series: [{
            name: 'Classificação',
            colorByPoint: true,
            data: serie
          }],
          drilldown: {
            series: subserie.map(s => ({
              ...s,
              dataLabels: {
                enabled: true,
                distance: 10,
                connectorWidth: 1,
                connectorPadding: 3,
                style: {
                  color: theme.textColor,
                  fontWeight: '600',
                  fontSize: '11px',
                  textOutline: 'none'
                },
                format: '{point.name}: {point.y:.1f}%'
              }
            }))
          }
        }
      } else if (this.currentType === 'donut') {
        chartConfig = {
          chart: {
            type: 'pie',
            backgroundColor: theme.backgroundColor,
            height: 400,
            marginTop: 60,
            marginBottom: 60,
            style: { fontFamily: '"Montserrat", sans-serif' },
            events: { render: function () { colorConnectors(this) } }
          },
          plotOptions: {
            pie: {
              allowPointSelect: true,
              cursor: 'pointer',
              innerSize: '60%',
              borderRadius: 8,
              borderWidth: 3,
              borderColor: theme.backgroundColor,
              center: ['50%', '50%'],
              size: '85%',
              dataLabels: {
                enabled: true,
                distance: 15,
                connectorWidth: 1,
                connectorPadding: 5,
                style: {
                  color: theme.textColor,
                  fontWeight: '600',
                  fontSize: '11px',
                  textOutline: 'none'
                },
                format: '{point.name}: {point.y:.1f}%'
              }
            }
          },
          series: [{
            name: 'Classificação',
            colorByPoint: true,
            data: serie
          }],
          drilldown: {
            series: subserie.map(s => ({
              ...s,
              type: 'pie',
              innerSize: '60%',
              size: '85%',
              center: ['50%', '50%'],
              dataLabels: {
                enabled: true,
                distance: 15,
                connectorWidth: 1,
                connectorPadding: 5,
                style: {
                  color: theme.textColor,
                  fontWeight: '600',
                  fontSize: '11px',
                  textOutline: 'none'
                },
                format: '{point.name}: {point.y:.1f}%'
              }
            }))
          }
        }
      } else if (this.currentType === 'bar') {
        const sortedSerie = [...serie].sort((a, b) => (b.y || 0) - (a.y || 0))
        const categories = sortedSerie.map(item => item.name)
        const data = sortedSerie.map(item => ({
          name: item.name,
          y: item.y,
          rawValue: item.rawValue,
          count: item.count
        }))

        chartConfig = {
          chart: {
            type: 'column',
            backgroundColor: theme.backgroundColor,
            height: 400,
            marginBottom: 160,
            marginTop: 60,
            marginLeft: 70,
            marginRight: 20,
            style: { fontFamily: '"Montserrat", sans-serif' }
          },
          xAxis: {
            categories: categories,
            labels: {
              rotation: -45,
              overflow: 'allow',
              style: { color: theme.textColor, fontSize: '10px' }
            }
          },
          yAxis: {
            title: { text: yAxisTitle, style: { color: theme.textColor } },
            labels: { style: { color: theme.textColor } },
            gridLineColor: theme.gridLineColor
          },
          legend: { enabled: false },
          plotOptions: {
            column: {
              borderRadius: 6,
              borderWidth: 0
            }
          },
          series: [{
            type: 'column',
            name: 'Classificação',
            data: data,
            colorByPoint: true
          }]
        }
      } else if (this.currentType === 'line') {
        const sortedSerie = [...serie].sort((a, b) => (b.y || 0) - (a.y || 0))
        const categories = sortedSerie.map(item => item.name)
        const data = sortedSerie.map(item => ({
          name: item.name,
          y: item.y,
          rawValue: item.rawValue,
          count: item.count
        }))

        chartConfig = {
          chart: {
            type: 'line',
            backgroundColor: theme.backgroundColor,
            height: 400,
            marginBottom: 160,
            marginTop: 60,
            marginLeft: 70,
            marginRight: 20,
            style: { fontFamily: '"Montserrat", sans-serif' }
          },
          xAxis: {
            categories: categories,
            labels: {
              rotation: -45,
              overflow: 'allow',
              style: { color: theme.textColor, fontSize: '10px' }
            }
          },
          yAxis: {
            title: { text: yAxisTitle, style: { color: theme.textColor } },
            labels: { style: { color: theme.textColor } },
            gridLineColor: theme.gridLineColor
          },
          legend: { enabled: false },
          plotOptions: {
            line: {
              marker: { enabled: true, radius: 5 },
              lineWidth: 3
            }
          },
          series: [{
            type: 'line',
            name: 'Classificação',
            data: data,
            color: theme.colors[0]
          }]
        }
      }

      const baseConfig = {
        colors: theme.colors,
        credits: { enabled: false },
        title: {
          text: this.currentTitle,
          align: 'center',
          style: {
            color: theme.textColor,
            fontSize: '18px',
            fontWeight: '600'
          }
        },
        tooltip: {
          outside: true,
          backgroundColor: theme.backgroundColor,
          borderColor: theme.gridLineColor,
          borderRadius: 8,
          style: {
            color: theme.textColor,
            fontSize: '12px',
            zIndex: 9999
          },
          headerFormat: '',
          formatter: function() {
            const lines = [
              `<span style="color:${this.point.color}">${this.point.name}</span>`,
              `Porcentagem: <b>${Highcharts.numberFormat(this.y, 2, ',', '.')}%</b>`
            ]
            if (typeof this.point.rawValue === 'number') {
              lines.push(`Valor: <b>R$ ${Highcharts.numberFormat(this.point.rawValue, 2, ',', '.')}</b>`)
            }
            if (typeof this.point.count === 'number') {
              lines.push(`Quantidade: <b>${Highcharts.numberFormat(this.point.count, 0, ',', '.')}</b>`)
            }
            return lines.join('<br/>')
          }
        }
      }

      this.chartInstance = Highcharts.chart(this.$refs.targetChart, { ...baseConfig, ...chartConfig })

      this.$nextTick(() => {
        if (this.chartInstance) this.chartInstance.reflow()
      })
    }
  },
  mixins: [ChartMix, ChartThemeMixin],
  mounted() {
    this.createChart()
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
      this.chartInstance = null
    }
  },
  watch: {
    isDarkTheme: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      immediate: false
    },
    classifications: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      deep: true
    }
  }
}
</script>
